import axios from 'axios';

import type {
  CartV2OrderServicePersonalizationCreateModel,
  CartV2OrderServicePersonalizationDisplayModel,
  CartV2OrderServicesDetailsDisplayModel,
  PersonalizationCartItemClientDisplayModel,
  RetouchCartItemClientDisplayModel,
  CartV2OrderServiceDisplayModel,
  CartV2OrderServiceUpsertModel,
} from '@shared/models/carts/cartOrderSevices';

import type { identifier } from '@core/models/base';
import type { CartImageDisplayModel } from '@shared/models/carts/cartImages';
import type { JobOrderServiceDisplayModel } from '@shared/models/jobs/jobOrderServices';
import type { SubjectDisplayModel } from '@shared/models/subjects/subjects';

export const fetchCartOrderServices = async (jobId: identifier) =>
  (await axios.get<CartV2OrderServicesDetailsDisplayModel>(`/api/customer/jobs/${jobId}/cartOrderServices`)).data;

export const upsertRetouchCartOrderServices = async (jobId: identifier, services: CartV2OrderServiceUpsertModel[]) => {
  (await axios.post<CartV2OrderServiceDisplayModel>(`/api/customer/jobs/${jobId}/cartOrderServices`, services)).data;
};

export const upsertPersonalizationsCartOrderServices = async (
  jobId: identifier,
  services: CartV2OrderServicePersonalizationCreateModel[],
) => {
  (
    await axios.post<CartV2OrderServiceDisplayModel>(
      `/api/customer/jobs/${jobId}/cartOrderServices/presonalization`,
      services,
    )
  ).data;
};

export const deleteCartOrderService = async (jobId: identifier, cartServiceId: identifier) => {
  const result = await axios.delete(`/api/customer/jobs/${jobId}/cartOrderServices/${cartServiceId}`);
  return result;
};

export const getRetouchUpsertModels = (
  cartOrderServices: CartV2OrderServiceDisplayModel[] | undefined,
  cartImages: CartImageDisplayModel[],
  jobOrderService: JobOrderServiceDisplayModel,
) => {
  return cartImages.map((cartImage) => {
    let ret = {} as CartV2OrderServiceUpsertModel;

    const existingCartOrderService = cartOrderServices?.find((m) => {
      if (cartImage.isSubjectImage) {
        return m.subjectImageId === cartImage.subjectImageId;
      }
      return m.groupImageId === cartImage.groupImageId;
    });

    if (existingCartOrderService) {
      ret = { ...existingCartOrderService, enabled: true } as CartV2OrderServiceUpsertModel;
    } else {
      const { id, imageThumbnailUrl, isSubjectImage, ...cartImageData } = cartImage;
      const { orderServiceId, price } = jobOrderService;
      ret = {
        ...cartImageData,
        orderServiceId,
        price,
      } as CartV2OrderServiceUpsertModel;
    }
    return ret;
  });
};

const setRetouchValues = (
  jobOrderService: JobOrderServiceDisplayModel,
  subject: SubjectDisplayModel,
  cartId: number,
  existingData?: CartV2OrderServiceDisplayModel | null,
): RetouchCartItemClientDisplayModel =>
  ({
    subject,
    enabled: !!existingData,
    subjectId: subject.id,
    price: existingData ? existingData.price : jobOrderService.price,
    id: existingData?.id,
    cartId: cartId,
    orderServiceId: jobOrderService.orderServiceId,
  }) as RetouchCartItemClientDisplayModel;

const setPersonalizationValues = (
  subject: SubjectDisplayModel,
  cartId: number,
  existingData?: CartV2OrderServicePersonalizationDisplayModel | null,
  printServicePrice?: number,
  walletServicePrice?: number,
): PersonalizationCartItemClientDisplayModel =>
  ({
    subject,
    cartId: cartId,
    enabled: !!existingData,
    id: existingData?.id,
    subjectId: subject.id,
    nameOnAllWallets: existingData ? existingData?.nameOnWallets : false,
    nameOnAllPrints: existingData ? existingData?.nameOnAllPrints : true,
    yearOnAllWallets: existingData ? existingData?.yearOnWallets : false,
    yearOnAllPrints: existingData ? existingData?.yearOnAllPrints : true,
    price: existingData?.price || 10,
    nameOnAllPrintsPrice: existingData && existingData.yearOnAllPrints ? existingData.price : printServicePrice ?? 10,
    nameOnWalletsPrice: existingData && existingData.nameOnWallets ? existingData.price : walletServicePrice ?? 10,
    name: existingData ? existingData.nameToPrint : subject.firstName,
    year: existingData ? existingData.yearToPrint : new Date().getFullYear().toString(),
  }) as PersonalizationCartItemClientDisplayModel;

export const setupRetouchData = (
  orderServiceDetails: CartV2OrderServicesDetailsDisplayModel | undefined,
  subjects: SubjectDisplayModel[],
  cartId: number,
  jobOrderService: JobOrderServiceDisplayModel,
): RetouchCartItemClientDisplayModel[] => {
  const retouches = [] as RetouchCartItemClientDisplayModel[];
  orderServiceDetails?.retouches.forEach((retouch) => {
    const subject = subjects.find((subject) => subject.id === retouch.subjectId);
    retouches.push(setRetouchValues(jobOrderService, subject as SubjectDisplayModel, cartId, retouch));
  });

  subjects.forEach((subject) => {
    const alreadyExists = retouches.find((m) => m.subjectId === subject.id);
    if (!alreadyExists) retouches.push(setRetouchValues(jobOrderService, subject, cartId));
  });

  return retouches;
};

export const setupPersonalizationData = (
  orderServiceDetails: CartV2OrderServicesDetailsDisplayModel | undefined,
  subjects: SubjectDisplayModel[],
  cartDetailsId: number,
  jobOrderServices: JobOrderServiceDisplayModel[],
): PersonalizationCartItemClientDisplayModel[] => {
  const printServicePrice = jobOrderServices.find((j) => j.allowNameOnAllPrints)?.price;
  const walletServicePrice = jobOrderServices.find((j) => j.allowNameOnWallets)?.price;

  const personalizations = [] as PersonalizationCartItemClientDisplayModel[];
  orderServiceDetails?.personalizations.forEach((personalization) => {
    const subject = subjects.find((subject) => subject.id === personalization.subjectId);
    personalizations.push(setPersonalizationValues(subject as SubjectDisplayModel, cartDetailsId, personalization));
  });

  subjects.forEach((subject) => {
    const alreadyExists = personalizations.find((m) => m.subjectId === subject.id);
    if (!alreadyExists)
      personalizations.push(
        setPersonalizationValues(subject, cartDetailsId, undefined, printServicePrice, walletServicePrice),
      );
  });

  return personalizations;
};
